import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SkillDeckLogo from './SkillDeckLogo';

// La clé porte la version du tutoriel : quand on ajoute ou remanie une étape,
// l'incrémenter fait revoir le parcours à ceux qui l'avaient déjà terminé —
// sans quoi personne ne découvrirait jamais les nouveautés.
// v2 : ajout de l'étape « CRM client ».
const TUTORIAL_VERSION = 2;
const TUTORIAL_KEY = (userId) => `sd_onboarding_done_v${TUTORIAL_VERSION}_${userId || 'guest'}`;

export const hasSeenTutorial = (userId) => {
  try { return localStorage.getItem(TUTORIAL_KEY(userId)) === '1'; }
  catch { return false; }
};

export const markTutorialSeen = (userId) => {
  try { localStorage.setItem(TUTORIAL_KEY(userId), '1'); } catch {}
};

export const resetTutorial = (userId) => {
  try { localStorage.removeItem(TUTORIAL_KEY(userId)); } catch {}
};

// ─── CSS ────────────────────────────────────────────────────────────────────
const tutorialCSS = `
.sd-tut-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: var(--bg);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: var(--white);
  animation: sdTutFadeIn .35s ease-out;
  overflow: hidden;
}
@keyframes sdTutFadeIn { from { opacity: 0; } to { opacity: 1; } }

/* Aurora background — matches landing page */
.sd-tut-aurora { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
.sd-tut-blob {
  position: absolute; border-radius: 50%;
  filter: blur(120px); opacity: .35;
  animation: sdTutFloat 18s ease-in-out infinite;
}
.sd-tut-blob.b1 { width: 600px; height: 600px; background: var(--vio); top: -150px; left: -100px; }
.sd-tut-blob.b2 { width: 500px; height: 500px; background: #2563EB; bottom: -120px; right: -80px; animation-delay: -6s; }
.sd-tut-blob.b3 { width: 400px; height: 400px; background: var(--vio2); top: 40%; left: 50%; animation-delay: -12s; }
@keyframes sdTutFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%      { transform: translate(40px, -30px) scale(1.08); }
  66%      { transform: translate(-30px, 20px) scale(.95); }
}

/* Grid overlay */
.sd-tut-grid {
  position: absolute; inset: 0; pointer-events: none;
  background-image:
    linear-gradient(rgba(123,104,238,.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(123,104,238,.06) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, #000 30%, transparent 80%);
}

/* Close + skip */
.sd-tut-top {
  position: absolute; top: 24px; right: 24px; z-index: 10;
  display: flex; gap: 10px; align-items: center;
}
.sd-tut-skip {
  background: transparent; border: 1px solid rgba(255,255,255,.12);
  color: var(--muted2); padding: 8px 16px; border-radius: 8px;
  font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: inherit;
  transition: all .15s;
}
.sd-tut-skip:hover { color: var(--white); border-color: rgba(255,255,255,.25); }
.sd-tut-close {
  width: 36px; height: 36px; border-radius: 8px;
  background: transparent; border: 1px solid rgba(255,255,255,.12);
  color: var(--muted2); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all .15s;
}
.sd-tut-close:hover { color: var(--white); border-color: rgba(255,255,255,.25); }

.sd-tut-logo-top {
  position: absolute; top: 24px; left: 28px; z-index: 10;
  display: flex; align-items: center; gap: 10px;
  font-size: 14px; font-weight: 700; letter-spacing: -.4px;
}
.sd-tut-logo-top span { color: var(--vio2); }

/* Main stage */
.sd-tut-stage {
  position: relative; z-index: 5;
  width: 100%; max-width: 1100px;
  padding: 80px 32px 100px;
  display: grid; grid-template-columns: 1fr 1fr; gap: 60px;
  align-items: center;
}
@media (max-width: 900px) {
  .sd-tut-stage { grid-template-columns: 1fr; gap: 32px; padding: 80px 20px 110px; }
}

.sd-tut-text { display: flex; flex-direction: column; gap: 16px; }
.sd-tut-eyebrow {
  display: inline-block; align-self: flex-start;
  font-size: 11px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase;
  color: var(--vio2); padding: 5px 11px; border-radius: 999px;
  background: rgba(108,71,255,.12); border: 1px solid rgba(108,71,255,.28);
}
.sd-tut-title {
  font-size: clamp(28px, 4vw, 44px); font-weight: 800;
  line-height: 1.05; letter-spacing: -1px;
  color: var(--white);
}
.sd-tut-title .grad {
  background: linear-gradient(135deg, #C4B5FD 0%, #7B68EE 50%, #2563EB 100%);
  -webkit-background-clip: text; background-clip: text; color: transparent;
}
.sd-tut-sub { font-size: 15px; color: var(--muted2); line-height: 1.65; max-width: 480px; }
.sd-tut-bullets {
  display: flex; flex-direction: column; gap: 8px; margin-top: 8px;
  list-style: none; padding: 0;
}
.sd-tut-bullets li {
  display: flex; align-items: center; gap: 9px;
  font-size: 13.5px; color: #C4B5FD;
}
.sd-tut-bullets li::before {
  content: ''; width: 5px; height: 5px; border-radius: 50%;
  background: linear-gradient(135deg, #7B68EE, #2563EB);
  flex-shrink: 0;
}

/* Mockup card */
.sd-tut-mockup {
  position: relative;
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(16,16,58,.85) 0%, rgba(10,10,44,.85) 100%);
  border: 1px solid rgba(123,104,238,.22);
  box-shadow: 0 30px 80px -20px rgba(108,71,255,.35), 0 0 0 1px rgba(255,255,255,.02) inset;
  backdrop-filter: blur(10px);
  overflow: hidden;
  min-height: 360px;
  padding: 22px;
}
.sd-tut-mockup-glow {
  position: absolute; inset: -2px;
  background: linear-gradient(135deg, rgba(108,71,255,.4), transparent 50%, rgba(37,99,235,.4));
  border-radius: 16px;
  opacity: .5; z-index: -1; filter: blur(20px);
}

/* Slide enter animation */
.sd-tut-slide { animation: sdTutSlideIn .5s cubic-bezier(.22,.9,.3,1.05); }
@keyframes sdTutSlideIn {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Footer / nav */
.sd-tut-footer {
  position: absolute; bottom: 28px; left: 0; right: 0; z-index: 10;
  display: flex; align-items: center; justify-content: center; gap: 24px;
  padding: 0 32px;
}
.sd-tut-dots { display: flex; gap: 8px; align-items: center; }
.sd-tut-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: rgba(255,255,255,.12); transition: all .25s; cursor: pointer; border: none;
}
.sd-tut-dot.active { background: linear-gradient(135deg, #7B68EE, #2563EB); width: 24px; border-radius: 4px; }
.sd-tut-nav { display: flex; gap: 10px; align-items: center; }
.sd-tut-btn {
  padding: 10px 22px; border-radius: 9px;
  font-size: 13px; font-weight: 600; cursor: pointer; border: none;
  font-family: inherit; transition: all .15s;
  display: inline-flex; align-items: center; gap: 6px;
}
.sd-tut-btn-ghost { background: transparent; color: var(--muted2); border: 1px solid rgba(255,255,255,.12); }
.sd-tut-btn-ghost:hover { color: var(--white); border-color: rgba(255,255,255,.25); }
.sd-tut-btn-primary {
  background: linear-gradient(135deg, #7B68EE 0%, var(--vio) 100%);
  color: white;
  box-shadow: 0 8px 24px -6px rgba(108,71,255,.6);
}
.sd-tut-btn-primary:hover { box-shadow: 0 12px 32px -6px rgba(108,71,255,.8); transform: translateY(-1px); }
.sd-tut-btn:disabled { opacity: .35; cursor: not-allowed; }

/* ─── SLIDE 1 — Welcome ─── */
.sd-welcome-stage {
  grid-column: 1 / -1; text-align: center;
  display: flex; flex-direction: column; align-items: center; gap: 22px;
  padding: 20px 0;
}
.sd-welcome-logo {
  width: 88px; height: 88px; border-radius: 22px;
  background: linear-gradient(135deg, rgba(108,71,255,.2), rgba(37,99,235,.15));
  border: 1px solid rgba(123,104,238,.35);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 20px 50px -10px rgba(108,71,255,.5);
  animation: sdTutLogoFloat 4s ease-in-out infinite, sdTutLogoIn .6s cubic-bezier(.22,.9,.3,1.4);
}
@keyframes sdTutLogoFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
@keyframes sdTutLogoIn { from { opacity: 0; transform: scale(.5); } to { opacity: 1; transform: scale(1); } }

.sd-welcome-title {
  font-size: clamp(36px, 5vw, 56px); font-weight: 800;
  line-height: 1.05; letter-spacing: -1.5px;
  max-width: 760px;
}
.sd-welcome-sub {
  font-size: 16px; color: var(--muted2); line-height: 1.6; max-width: 560px;
}
.sd-welcome-sparkles {
  position: absolute; inset: 0; pointer-events: none; overflow: hidden;
}
.sd-spark {
  position: absolute; width: 4px; height: 4px; border-radius: 50%;
  background: #C4B5FD; box-shadow: 0 0 14px #7B68EE;
  animation: sdSparkFloat 6s ease-in-out infinite;
}

@keyframes sdSparkFloat {
  0%   { transform: translate(0,0); opacity: 0; }
  20%  { opacity: 1; }
  80%  { opacity: 1; }
  100% { transform: translate(var(--dx,30px), var(--dy,-40px)); opacity: 0; }
}

/* ─── SLIDE 2 — Upload CV ─── */
.sd-up-zone {
  border: 2px dashed rgba(123,104,238,.35);
  border-radius: 12px; padding: 28px 18px;
  text-align: center;
  background: rgba(10,10,44,.4);
  transition: all .5s;
  position: relative; overflow: hidden;
}
.sd-up-zone.active {
  border-color: #7B68EE;
  background: rgba(108,71,255,.08);
  box-shadow: 0 0 30px rgba(108,71,255,.25);
}
.sd-up-icon {
  width: 56px; height: 56px; margin: 0 auto 12px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(108,71,255,.25), rgba(37,99,235,.18));
  border: 1px solid rgba(123,104,238,.35);
  display: flex; align-items: center; justify-content: center;
  font-size: 28px;
  animation: sdUpBounce 1.6s ease-in-out infinite;
}
@keyframes sdUpBounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
.sd-up-text { font-size: 13px; color: #C4B5FD; font-weight: 600; }
.sd-up-sub { font-size: 11.5px; color: var(--muted); margin-top: 4px; }
.sd-up-file {
  margin-top: 16px;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 13px; border-radius: 8px;
  background: rgba(108,71,255,.12); border: 1px solid rgba(123,104,238,.35);
  font-size: 12px; color: var(--white); font-weight: 500;
  opacity: 0; transform: translateY(6px);
  transition: all .4s ease-out;
}
.sd-up-file.show { opacity: 1; transform: translateY(0); }
.sd-up-file-icon { color: var(--vio2); }
.sd-up-progress { margin-top: 14px; height: 3px; background: rgba(255,255,255,.05); border-radius: 3px; overflow: hidden; }
.sd-up-progress-fill {
  height: 100%; width: 0%;
  background: linear-gradient(90deg, #7B68EE, #2563EB);
  transition: width 1.8s ease-out;
}
.sd-up-progress-fill.go { width: 100%; }

/* ─── SLIDE 3 — AI Extract ─── */
.sd-prof-card {
  background: linear-gradient(180deg, rgba(16,16,58,.6), rgba(10,10,44,.6));
  border: 1px solid rgba(123,104,238,.25);
  border-radius: 12px; padding: 16px;
}
.sd-prof-head { display: flex; align-items: center; gap: 12px; }
.sd-prof-av {
  width: 44px; height: 44px; border-radius: 12px;
  background: linear-gradient(135deg, #7B68EE, #2563EB);
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; color: white; font-size: 14px;
}
.sd-prof-name { font-size: 14px; font-weight: 700; color: var(--white); }
.sd-prof-role { font-size: 11.5px; color: var(--muted2); margin-top: 2px; }
.sd-prof-status {
  margin-top: 14px; padding: 10px 12px; border-radius: 8px;
  background: rgba(108,71,255,.08); border: 1px solid rgba(123,104,238,.2);
  display: flex; align-items: center; gap: 9px;
  font-size: 11.5px; color: #C4B5FD;
}
.sd-prof-status-dot {
  width: 7px; height: 7px; border-radius: 50%; background: var(--vio2);
  animation: sdPulse 1.3s ease-in-out infinite;
}
@keyframes sdPulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .35; transform: scale(.85); } }
.sd-prof-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 14px; }
.sd-prof-tag {
  padding: 4px 10px; border-radius: 999px;
  font-size: 11px; font-weight: 600;
  background: rgba(108,71,255,.14); color: #C4B5FD;
  border: 1px solid rgba(123,104,238,.25);
  opacity: 0; transform: scale(.7);
  transition: all .35s cubic-bezier(.22,.9,.3,1.4);
}
.sd-prof-tag.show { opacity: 1; transform: scale(1); }

.sd-prof-exp { margin-top: 14px; }
.sd-prof-exp-row {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 0; border-bottom: 1px dashed rgba(123,104,238,.12);
  opacity: 0; transform: translateX(-8px);
  transition: all .35s ease-out;
}
.sd-prof-exp-row.show { opacity: 1; transform: translateX(0); }
.sd-prof-exp-row:last-child { border-bottom: none; }
.sd-prof-exp-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: linear-gradient(135deg, #7B68EE, #2563EB);
  flex-shrink: 0;
}
.sd-prof-exp-text { flex: 1; font-size: 11.5px; color: #C4B5FD; font-weight: 500; }
.sd-prof-exp-meta { font-size: 10.5px; color: var(--muted); }

/* ─── SLIDE 4 — Customize & Export ─── */
.sd-templates { display: grid; grid-template-columns: repeat(3, 1fr); gap: 9px; }
.sd-tpl {
  aspect-ratio: 3/4; border-radius: 9px;
  background: linear-gradient(180deg, rgba(255,255,255,.04), rgba(255,255,255,.01));
  border: 1px solid rgba(123,104,238,.15);
  padding: 10px 8px;
  display: flex; flex-direction: column; gap: 5px;
  position: relative; overflow: hidden;
  cursor: pointer; transition: all .25s;
  opacity: 0; transform: translateY(10px);
  animation: sdTplIn .4s ease-out forwards;
}
.sd-tpl:nth-child(1) { animation-delay: 0s; }
.sd-tpl:nth-child(2) { animation-delay: .08s; }
.sd-tpl:nth-child(3) { animation-delay: .16s; }
@keyframes sdTplIn { to { opacity: 1; transform: translateY(0); } }
.sd-tpl.active {
  border-color: #7B68EE;
  box-shadow: 0 0 0 1px #7B68EE, 0 8px 24px -8px rgba(108,71,255,.5);
}
.sd-tpl-head { height: 5px; border-radius: 2px; background: var(--tpl-c, #7B68EE); }
.sd-tpl-line { height: 3px; border-radius: 2px; background: rgba(255,255,255,.08); }
.sd-tpl-line.short { width: 60%; }
.sd-tpl-check {
  position: absolute; top: 6px; right: 6px;
  width: 16px; height: 16px; border-radius: 50%;
  background: linear-gradient(135deg, #7B68EE, #2563EB);
  display: flex; align-items: center; justify-content: center;
  font-size: 9px; color: white; font-weight: 700;
  opacity: 0; transform: scale(.5);
  transition: all .25s cubic-bezier(.22,.9,.3,1.4);
}
.sd-tpl.active .sd-tpl-check { opacity: 1; transform: scale(1); }

.sd-colors { display: flex; gap: 8px; margin-top: 14px; align-items: center; }
.sd-color-label { font-size: 11px; color: var(--muted2); font-weight: 500; margin-right: 4px; }
.sd-color-dot {
  width: 22px; height: 22px; border-radius: 50%;
  cursor: pointer; border: 2px solid transparent;
  transition: all .2s;
}
.sd-color-dot.active { border-color: var(--white); transform: scale(1.08); }
.sd-color-dot.c1 { background: #7B68EE; }
.sd-color-dot.c2 { background: #2563EB; }
.sd-color-dot.c3 { background: #10B981; }
.sd-color-dot.c4 { background: #F97316; }
.sd-color-dot.c5 { background: #EC4899; }

.sd-exports { display: flex; gap: 8px; margin-top: 16px; }
.sd-export-btn {
  flex: 1; padding: 9px 11px; border-radius: 8px;
  background: rgba(108,71,255,.1); border: 1px solid rgba(123,104,238,.25);
  font-size: 11.5px; font-weight: 600; color: #C4B5FD;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  cursor: pointer; transition: all .2s;
  position: relative; overflow: hidden;
}
.sd-export-btn:hover { background: rgba(108,71,255,.2); color: var(--white); }
.sd-export-btn::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,.15) 50%, transparent 70%);
  transform: translateX(-100%); transition: transform .6s;
}
.sd-export-btn.shine::after { transform: translateX(100%); }

/* ─── SLIDE 5 — AI Matching ─── */
.sd-match-scene {
  display: grid; grid-template-columns: 1fr auto 1fr;
  gap: 16px; align-items: center;
}
.sd-match-card {
  background: linear-gradient(180deg, rgba(16,16,58,.6), rgba(10,10,44,.6));
  border: 1px solid rgba(123,104,238,.25);
  border-radius: 10px; padding: 12px;
  font-size: 11px;
}
.sd-match-label { font-size: 9.5px; color: var(--muted); text-transform: uppercase; letter-spacing: .6px; font-weight: 700; margin-bottom: 6px; }
.sd-match-title { font-size: 12.5px; font-weight: 700; color: var(--white); }
.sd-match-meta { font-size: 10.5px; color: var(--muted2); margin-top: 3px; }
.sd-match-engine {
  width: 70px; height: 70px; border-radius: 50%;
  background: linear-gradient(135deg, rgba(108,71,255,.25), rgba(37,99,235,.18));
  border: 1px solid rgba(123,104,238,.35);
  display: flex; align-items: center; justify-content: center;
  position: relative;
  animation: sdEngineSpin 8s linear infinite;
}
@keyframes sdEngineSpin { to { transform: rotate(360deg); } }
.sd-match-engine::before {
  content: ''; position: absolute; inset: -6px;
  border-radius: 50%; border: 1px dashed rgba(123,104,238,.4);
}
.sd-match-engine-inner {
  width: 36px; height: 36px; border-radius: 50%;
  background: linear-gradient(135deg, #7B68EE, #2563EB);
  display: flex; align-items: center; justify-content: center;
  color: white; font-size: 16px; font-weight: 700;
  animation: sdEngineCounter 8s linear infinite;
}
@keyframes sdEngineCounter { to { transform: rotate(-360deg); } }

.sd-match-score {
  margin-top: 14px;
  padding: 14px 16px; border-radius: 10px;
  background: linear-gradient(135deg, rgba(34,197,94,.12), rgba(108,71,255,.1));
  border: 1px solid rgba(34,197,94,.3);
  display: flex; justify-content: space-between; align-items: center;
}
.sd-match-score-label { font-size: 11.5px; color: #C4B5FD; font-weight: 500; }
.sd-match-score-val {
  font-size: 24px; font-weight: 800;
  background: linear-gradient(135deg, #4ADE80, var(--vio2));
  -webkit-background-clip: text; background-clip: text; color: transparent;
}

/* ─── SLIDE 6 — CRM client ─── */
.sd-crm-tree { display: flex; flex-direction: column; gap: 8px; }
.sd-crm-node {
  background: linear-gradient(180deg, rgba(16,16,58,.6), rgba(10,10,44,.6));
  border: 1px solid rgba(123,104,238,.25);
  border-radius: 10px; padding: 10px 12px;
  display: flex; align-items: center; gap: 10px;
  animation: sdCrmIn .45s ease-out both;
}
.sd-crm-node.lvl1 { margin-left: 18px; }
.sd-crm-node.lvl2 { margin-left: 36px; }
@keyframes sdCrmIn { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: none; } }
.sd-crm-ic {
  width: 26px; height: 26px; border-radius: 8px; flex-shrink: 0;
  background: rgba(123,104,238,.18); color: #C4B5FD;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 800;
}
.sd-crm-nm { font-size: 12px; font-weight: 700; color: var(--white); }
.sd-crm-sub { font-size: 10.5px; color: var(--muted2); margin-top: 2px; }
.sd-crm-pill {
  margin-left: auto; flex-shrink: 0;
  font-size: 9.5px; font-weight: 800; padding: 3px 9px; border-radius: 20px;
  background: rgba(74,222,128,.14); color: #4ADE80;
}
.sd-crm-pill.warn { background: rgba(250,204,21,.14); color: #FACC15; }
.sd-crm-relance {
  margin-top: 14px; padding: 12px 14px; border-radius: 10px;
  background: linear-gradient(135deg, rgba(250,204,21,.12), rgba(108,71,255,.1));
  border: 1px solid rgba(250,204,21,.3);
  font-size: 11.5px; color: #FDE68A; font-weight: 600;
}

/* ─── SLIDE 7 — Ready ─── */
.sd-ready-stage {
  grid-column: 1 / -1; text-align: center;
  display: flex; flex-direction: column; align-items: center; gap: 22px;
}
.sd-ready-check {
  width: 96px; height: 96px; border-radius: 50%;
  background: linear-gradient(135deg, #22C55E, #4ADE80);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 20px 60px -10px rgba(34,197,94,.5);
  animation: sdReadyPop .6s cubic-bezier(.22,.9,.3,1.5);
  position: relative;
}
@keyframes sdReadyPop { from { opacity: 0; transform: scale(.4) rotate(-20deg); } to { opacity: 1; transform: scale(1) rotate(0); } }
.sd-ready-check::before {
  content: ''; position: absolute; inset: -8px;
  border-radius: 50%; border: 2px solid rgba(34,197,94,.35);
  animation: sdReadyRing 1.6s ease-out infinite;
}
@keyframes sdReadyRing { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(1.4); opacity: 0; } }
.sd-ready-check svg { color: white; }
`;

// ─── Animated welcome sparkles ──────────────────────────────────────────────
const sparkPositions = [
  { left: '15%', top: '20%', dx: '40px',  dy: '-60px', delay: '0s' },
  { left: '75%', top: '25%', dx: '-30px', dy: '-50px', delay: '.6s' },
  { left: '10%', top: '75%', dx: '50px',  dy: '40px',  delay: '1.2s' },
  { left: '80%', top: '70%', dx: '-40px', dy: '60px',  delay: '1.8s' },
  { left: '50%', top: '15%', dx: '0px',   dy: '-70px', delay: '2.4s' },
  { left: '30%', top: '85%', dx: '30px',  dy: '50px',  delay: '3s' },
];

// ─── SLIDE 1 — Welcome ──────────────────────────────────────────────────────
const SlideWelcome = ({ userName }) => (
  <div className="sd-welcome-stage sd-tut-slide">
    <div className="sd-welcome-sparkles">
      {sparkPositions.map((p, i) => (
        <span key={i} className="sd-spark" style={{
          left: p.left, top: p.top,
          '--dx': p.dx, '--dy': p.dy,
          animationDelay: p.delay,
        }} />
      ))}
    </div>
    <div className="sd-welcome-logo">
      <SkillDeckLogo size={56} />
    </div>
    <h1 className="sd-welcome-title">
      Bienvenue dans <span style={{
        background: 'linear-gradient(135deg, #C4B5FD 0%, #7B68EE 50%, #2563EB 100%)',
        WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
      }}>SkillDeck</span>
      {userName ? <>, <br />{userName}</> : ''}
    </h1>
    <p className="sd-welcome-sub">
      Découvrez en 1 minute comment transformer n'importe quel CV
      en dossier de compétences professionnel — prêt à convaincre vos clients.
    </p>
  </div>
);

// ─── SLIDE 2 — Upload CV ────────────────────────────────────────────────────
const SlideUpload = () => {
  const [fileShow, setFileShow] = useState(false);
  const [progressGo, setProgressGo] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setFileShow(true), 500);
    const t2 = setTimeout(() => setProgressGo(true), 900);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return (
    <>
      <div className="sd-tut-text sd-tut-slide">
        <span className="sd-tut-eyebrow">Étape 1 · Importer</span>
        <h2 className="sd-tut-title">
          Glissez un CV,<br />
          <span className="grad">l'IA fait le reste.</span>
        </h2>
        <p className="sd-tut-sub">
          PDF, Word, scanné ou structuré — peu importe la mise en page.
          Notre IA analyse et extrait les informations en quelques secondes.
        </p>
        <ul className="sd-tut-bullets">
          <li>PDF et Word natifs</li>
          <li>CVs scannés et photos</li>
          <li>Tous les formats de mise en page</li>
        </ul>
      </div>
      <div className="sd-tut-mockup sd-tut-slide" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div className="sd-tut-mockup-glow" />
        <div className={`sd-up-zone ${fileShow ? 'active' : ''}`}>
          <div className="sd-up-icon">📄</div>
          <div className="sd-up-text">Déposez votre CV ici</div>
          <div className="sd-up-sub">ou cliquez pour parcourir</div>
          <div className={`sd-up-file ${fileShow ? 'show' : ''}`}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" className="sd-up-file-icon">
              <path d="M3 1.5h5L11 4.5v8a1 1 0 01-1 1H3a1 1 0 01-1-1V2.5a1 1 0 011-1z" />
              <path d="M8 1.5v3h3" />
            </svg>
            Thomas_Mercier_CV.pdf
          </div>
          <div className="sd-up-progress">
            <div className={`sd-up-progress-fill ${progressGo ? 'go' : ''}`} />
          </div>
        </div>
      </div>
    </>
  );
};

// ─── SLIDE 3 — AI Extract ───────────────────────────────────────────────────
const SlideExtract = () => {
  const [tagShow, setTagShow] = useState([false, false, false, false, false]);
  const [expShow, setExpShow] = useState([false, false, false]);
  useEffect(() => {
    const ts = [];
    [0, 1, 2, 3, 4].forEach((i) =>
      ts.push(setTimeout(() => setTagShow((p) => { const n = [...p]; n[i] = true; return n; }), 300 + i * 130))
    );
    [0, 1, 2].forEach((i) =>
      ts.push(setTimeout(() => setExpShow((p) => { const n = [...p]; n[i] = true; return n; }), 1200 + i * 220))
    );
    return () => ts.forEach(clearTimeout);
  }, []);
  return (
    <>
      <div className="sd-tut-text sd-tut-slide">
        <span className="sd-tut-eyebrow">Étape 2 · Extraction IA</span>
        <h2 className="sd-tut-title">
          Compétences,<br />
          <span className="grad">expériences, missions.</span>
        </h2>
        <p className="sd-tut-sub">
          L'IA structure automatiquement le profil : stack technique, postes,
          missions, formations. Vous restez maître de la version finale —
          tout est éditable.
        </p>
        <ul className="sd-tut-bullets">
          <li>Stack technique catégorisée</li>
          <li>Expériences hiérarchisées</li>
          <li>Édition libre avant publication</li>
        </ul>
      </div>
      <div className="sd-tut-mockup sd-tut-slide">
        <div className="sd-tut-mockup-glow" />
        <div className="sd-prof-card">
          <div className="sd-prof-head">
            <div className="sd-prof-av">TM</div>
            <div>
              <div className="sd-prof-name">Thomas Mercier</div>
              <div className="sd-prof-role">Lead Dev Full Stack · 7 ans d'exp.</div>
            </div>
          </div>
          <div className="sd-prof-status">
            <span className="sd-prof-status-dot" />
            Analyse IA — extraction des compétences…
          </div>
          <div className="sd-prof-tags">
            {['React', 'Node.js', 'AWS', 'PostgreSQL', 'Docker'].map((t, i) => (
              <span key={t} className={`sd-prof-tag ${tagShow[i] ? 'show' : ''}`}>{t}</span>
            ))}
          </div>
          <div className="sd-prof-exp">
            {[
              { t: 'Lead Dev — FinTech SaaS', m: '2022 — Présent' },
              { t: 'Senior Full Stack — Agence', m: '2019 — 2022' },
              { t: 'Développeur — Start-up', m: '2017 — 2019' },
            ].map((e, i) => (
              <div key={i} className={`sd-prof-exp-row ${expShow[i] ? 'show' : ''}`}>
                <span className="sd-prof-exp-dot" />
                <span className="sd-prof-exp-text">{e.t}</span>
                <span className="sd-prof-exp-meta">{e.m}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

// ─── SLIDE 4 — Customize & Export ───────────────────────────────────────────
const SlideExport = () => {
  const [activeTpl, setActiveTpl] = useState(1);
  const [activeColor, setActiveColor] = useState(0);
  const [shine, setShine] = useState(-1);
  useEffect(() => {
    let i = 0;
    const tplIv = setInterval(() => {
      i = (i + 1) % 3;
      setActiveTpl(i);
    }, 1400);
    let c = 0;
    const colorIv = setInterval(() => {
      c = (c + 1) % 5;
      setActiveColor(c);
    }, 1100);
    let s = 0;
    const shineIv = setInterval(() => {
      setShine(s);
      s = (s + 1) % 3;
    }, 900);
    return () => { clearInterval(tplIv); clearInterval(colorIv); clearInterval(shineIv); };
  }, []);
  const tplColors = ['#7B68EE', '#2563EB', '#EC4899'];
  return (
    <>
      <div className="sd-tut-text sd-tut-slide">
        <span className="sd-tut-eyebrow">Étape 3 · Personnaliser</span>
        <h2 className="sd-tut-title">
          Votre branding,<br />
          <span className="grad">vos couleurs, votre style.</span>
        </h2>
        <p className="sd-tut-sub">
          20 templates professionnels, couleurs personnalisables, logo de votre
          organisation. Exportez en PDF, PowerPoint, ou partagez via un lien
          sécurisé.
        </p>
        <ul className="sd-tut-bullets">
          <li>20+ templates pro</li>
          <li>Palette de couleurs par dossier</li>
          <li>PDF, PPT, ou lien partageable</li>
        </ul>
      </div>
      <div className="sd-tut-mockup sd-tut-slide">
        <div className="sd-tut-mockup-glow" />
        <div className="sd-templates">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`sd-tpl ${activeTpl === i ? 'active' : ''}`} style={{ '--tpl-c': tplColors[i] }}>
              <div className="sd-tpl-head" />
              <div className="sd-tpl-line" />
              <div className="sd-tpl-line short" />
              <div className="sd-tpl-line" />
              <div className="sd-tpl-line short" />
              <div className="sd-tpl-line" />
              <div className="sd-tpl-check">✓</div>
            </div>
          ))}
        </div>
        <div className="sd-colors">
          <span className="sd-color-label">Couleur</span>
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className={`sd-color-dot c${i + 1} ${activeColor === i ? 'active' : ''}`} />
          ))}
        </div>
        <div className="sd-exports">
          <button className={`sd-export-btn ${shine === 0 ? 'shine' : ''}`}>📄 PDF</button>
          <button className={`sd-export-btn ${shine === 1 ? 'shine' : ''}`}>📊 PPT</button>
          <button className={`sd-export-btn ${shine === 2 ? 'shine' : ''}`}>🔗 Lien</button>
        </div>
      </div>
    </>
  );
};

// ─── SLIDE 5 — AI Matching ──────────────────────────────────────────────────
const SlideMatching = () => {
  const [score, setScore] = useState(0);
  useEffect(() => {
    let target = 92;
    let curr = 0;
    const iv = setInterval(() => {
      curr += 2;
      if (curr >= target) { curr = target; clearInterval(iv); }
      setScore(curr);
    }, 28);
    return () => clearInterval(iv);
  }, []);
  return (
    <>
      <div className="sd-tut-text sd-tut-slide">
        <span className="sd-tut-eyebrow">Étape 4 · IA Matching · Bêta</span>
        <h2 className="sd-tut-title">
          Le bon profil,<br />
          <span className="grad">au bon moment.</span>
        </h2>
        <p className="sd-tut-sub">
          Importez un appel d'offres, et SkillDeck calcule en quelques secondes
          un score de compatibilité pour chaque consultant. Plus de temps perdu
          à chercher le profil idéal.
        </p>
        <ul className="sd-tut-bullets">
          <li>Analyse sémantique du cahier des charges</li>
          <li>Score de matching par consultant</li>
          <li>Shortlist prête à envoyer</li>
        </ul>
      </div>
      <div className="sd-tut-mockup sd-tut-slide">
        <div className="sd-tut-mockup-glow" />
        <div className="sd-match-scene">
          <div className="sd-match-card">
            <div className="sd-match-label">Consultant</div>
            <div className="sd-match-title">Thomas M.</div>
            <div className="sd-match-meta">Lead Dev · React/AWS</div>
          </div>
          <div className="sd-match-engine">
            <div className="sd-match-engine-inner">✨</div>
          </div>
          <div className="sd-match-card">
            <div className="sd-match-label">Appel d'offres</div>
            <div className="sd-match-title">Migration cloud</div>
            <div className="sd-match-meta">Banque · 6 mois</div>
          </div>
        </div>
        <div className="sd-match-score">
          <span className="sd-match-score-label">Score de compatibilité</span>
          <span className="sd-match-score-val">{score}%</span>
        </div>
      </div>
    </>
  );
};

// ─── SLIDE 6 — CRM client ───────────────────────────────────────────────────
const SlideCrm = () => (
  <>
    <div className="sd-tut-text sd-tut-slide">
      <span className="sd-tut-eyebrow">Étape 5 · CRM client</span>
      <h2 className="sd-tut-title">
        Vos comptes,<br />
        <span className="grad">équipe par équipe.</span>
      </h2>
      <p className="sd-tut-sub">
        Cartographiez chaque grand compte : ses entités, ses équipes, et les
        interlocuteurs qui comptent. SkillDeck vous rappelle qui n'a pas été
        contacté depuis trop longtemps.
      </p>
      <ul className="sd-tut-bullets">
        <li>Compte, entité, équipe : le périmètre en clair</li>
        <li>Fiche client avec historique des échanges</li>
        <li>Relances signalées automatiquement</li>
      </ul>
    </div>
    <div className="sd-tut-mockup sd-tut-slide">
      <div className="sd-tut-mockup-glow" />
      <div className="sd-crm-tree">
        <div className="sd-crm-node" style={{ animationDelay: '0s' }}>
          <span className="sd-crm-ic">BP</span>
          <div>
            <div className="sd-crm-nm">BNP Paribas</div>
            <div className="sd-crm-sub">Banque · Paris</div>
          </div>
        </div>
        <div className="sd-crm-node lvl1" style={{ animationDelay: '.12s' }}>
          <span className="sd-crm-ic">◧</span>
          <div>
            <div className="sd-crm-nm">BNP CIB</div>
            <div className="sd-crm-sub">2 équipes</div>
          </div>
        </div>
        <div className="sd-crm-node lvl2" style={{ animationDelay: '.24s' }}>
          <span className="sd-crm-ic">◇</span>
          <div>
            <div className="sd-crm-nm">Cash Management</div>
            <div className="sd-crm-sub">Inès Laval · Responsable</div>
          </div>
          <span className="sd-crm-pill">Active</span>
        </div>
        <div className="sd-crm-node lvl2" style={{ animationDelay: '.36s' }}>
          <span className="sd-crm-ic">◇</span>
          <div>
            <div className="sd-crm-nm">Services Paiements</div>
            <div className="sd-crm-sub">Aucun contact référencé</div>
          </div>
          <span className="sd-crm-pill warn">Cible</span>
        </div>
      </div>
      <div className="sd-crm-relance">
        ⏰ 3 clients sans contact depuis plus de 30 jours
      </div>
    </div>
  </>
);

// ─── SLIDE 7 — Ready ────────────────────────────────────────────────────────
const SlideReady = () => (
  <div className="sd-ready-stage sd-tut-slide">
    <div className="sd-ready-check">
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
    <h1 className="sd-welcome-title">
      Tout est <span style={{
        background: 'linear-gradient(135deg, #4ADE80 0%, #7B68EE 60%, #2563EB 100%)',
        WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
      }}>prêt.</span>
    </h1>
    <p className="sd-welcome-sub">
      Lancez-vous : importez votre premier CV et générez un dossier de compétences
      en moins de 2 minutes. Vous pourrez rejouer ce tutoriel à tout moment depuis
      votre menu utilisateur.
    </p>
  </div>
);

// ─── Main component ────────────────────────────────────────────────────────
const SLIDES = [
  { component: SlideWelcome },
  { component: SlideUpload },
  { component: SlideExtract },
  { component: SlideExport },
  { component: SlideMatching },
  { component: SlideCrm },
  { component: SlideReady },
];

const OnboardingTutorial = ({ open, onClose, onComplete }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [slideKey, setSlideKey] = useState(0);
  const overlayRef = useRef(null);

  // Reset step when re-opening
  useEffect(() => {
    if (open) {
      setStep(0);
      setSlideKey((k) => k + 1);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Keyboard nav
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  const handleClose = () => {
    markTutorialSeen(user?.id);
    onClose?.();
  };

  const handleFinish = () => {
    markTutorialSeen(user?.id);
    onComplete?.();
    onClose?.();
    navigate('/dossier/new');
  };

  const next = () => {
    if (step < SLIDES.length - 1) {
      setStep((s) => s + 1);
      setSlideKey((k) => k + 1);
    } else {
      handleFinish();
    }
  };
  const prev = () => {
    if (step > 0) {
      setStep((s) => s - 1);
      setSlideKey((k) => k + 1);
    }
  };
  const goTo = (i) => {
    setStep(i);
    setSlideKey((k) => k + 1);
  };

  if (!open) return null;

  const Slide = SLIDES[step].component;
  const isLast = step === SLIDES.length - 1;
  const userName = user?.user_metadata?.full_name?.split(' ')[0] || '';

  return (
    <div className="sd-tut-overlay" ref={overlayRef} role="dialog" aria-modal="true" aria-label="Tutoriel d'introduction">
      <style>{tutorialCSS}</style>

      <div className="sd-tut-aurora">
        <div className="sd-tut-blob b1" />
        <div className="sd-tut-blob b2" />
        <div className="sd-tut-blob b3" />
      </div>
      <div className="sd-tut-grid" />

      <div className="sd-tut-logo-top">
        <SkillDeckLogo size={28} />
        <span>Skill<span>Deck</span></span>
      </div>

      <div className="sd-tut-top">
        <button className="sd-tut-skip" onClick={handleClose}>
          Passer le tutoriel
        </button>
        <button className="sd-tut-close" onClick={handleClose} aria-label="Fermer">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M2 2l10 10M12 2L2 12" />
          </svg>
        </button>
      </div>

      <div key={slideKey} className="sd-tut-stage">
        <Slide userName={userName} />
      </div>

      <div className="sd-tut-footer">
        <button
          className="sd-tut-btn sd-tut-btn-ghost"
          onClick={prev}
          disabled={step === 0}
          style={step === 0 ? { visibility: 'hidden' } : undefined}
        >
          ← Précédent
        </button>

        <div className="sd-tut-dots">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              className={`sd-tut-dot ${i === step ? 'active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Étape ${i + 1}`}
            />
          ))}
        </div>

        <button className="sd-tut-btn sd-tut-btn-primary" onClick={next}>
          {isLast ? 'Créer mon premier dossier →' : 'Suivant →'}
        </button>
      </div>
    </div>
  );
};

export default OnboardingTutorial;
